import {
  Button,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Select,
} from '@/components'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import React, { useEffect, useState } from 'react'
import DataTable from 'react-data-table-component'
import { useLocation, useNavigate, } from 'react-router-dom'
import { Col } from 'styled-bootstrap-grid'
import commonUtils from '@/utils/commonUtils'
import { useRedis } from '@/hooks/useRedis'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

interface CodeDetailItem {
  cmpnyCd: string
  codeType: string
  codeTypeNm: string
  code: string
  codeNm: string
  engCodeNm: string
  displayOrder: number
  mainTable: string
  mainColumn: string
  upCode: string
  upCodeType: string
  useYn: string
  remark: string
  regId: string
  regDate: string
  updId: string
  updDate: string
}

const DetailComCode: React.FC = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const location = useLocation();

  const { getComCodes, getComCodeValue } = useRedis();

  const { showAlert, showConfirm } = useDialog();
  const [requestData, setRequestData] = useState({
    codeType: location.state?.codeType || '',
  });

  const [commonData, setCommonData] = useState<CodeDetailItem>({
    cmpnyCd: '',
    codeType: '',
    codeTypeNm: '',
    code: '',
    codeNm: '',
    engCodeNm: '',
    displayOrder: 1,
    mainTable: '',
    mainColumn: '',
    upCode: '',
    upCodeType: '',
    useYn: 'Y',
    remark: '',
    regId: '',
    regDate: '',
    updId: '',
    updDate: '',
  });
  const [codes, setCodes] = useState<CodeDetailItem[]>([]);
  const [originCodes, setOriginCodes] = useState<CodeDetailItem[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [addMode, setAddMode] = useState(false);

  const getCodeDetail = async (codeType?: string) => {
    try {
      const response = await axios.post('/comCode/detail', {
        codeType: codeType || requestData.codeType,
      })
      if (response.status == 200) {
        const codeData = response.data.data.data
        if (codeData) {
          setCodes(codeData);
          setOriginCodes(codeData);
          setCommonData(codeData[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching terms info:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target;
    setCommonData((prevState) => {
      return {
        ...prevState,
        [name]: value,
      } as CodeDetailItem // 모든 필드를 포함하는 상태를 유지
    })
  }

  const handleRowChange = (e: React.ChangeEvent<TInput>, idx: number) => {
    const { name, value } = e.target;

    setCodes(prevItems => {
      if (prevItems.length === 0) return prevItems; // 배열이 비어있으면 그대로 반환

      const newItems = [...prevItems]; // 기존 배열을 복사 (불변성 유지)
      newItems[idx] = { ...newItems[idx], [name]: value }; // 첫 번째 항목만 수정

      return newItems;
    })

  }

  const changePage = (page: number, add?: boolean) => {
    // 페이지 변경시 원본데이터로 엎어치기
    setCodes(originCodes);
    setCurrentPage(page);
    if (add) {
      setCodes([
        {
          ...codes[0]
          , code: ''
          , codeNm: ''
          , engCodeNm: ''
          , displayOrder: 1
          , remark: ''
          , useYn: 'Y'
          , regId: ''
          , regDate: ''
          , updId: ''
          , updDate: ''
        },
        ...codes,
      ]);
    }
  }

  const validate = (data: CodeDetailItem) => {
    const validateInfo: any = {
      codeType: {
        type: 's',
        require: true,
        name: '코드유형',
      },
      code: {
        type: 's',
        require: true,
        name: '코드',
      },
    }
    let validateResult = true
    Object.entries(data).forEach(([key, value]) => {
      if (validateResult && validateInfo[key]) {
        // 알림창 1회만 노출되도록
        if (validateInfo[key].require && (!value || value.trim() === '')) {
          // 필수값인경우
          const name = validateInfo[key].name || ''
          const code = name.charCodeAt(name.length - 1)
          if (code < 0xac00 || code > 0xd7a3 || (code - 0xac00) % 28 === 0) {
            showAlert({
              type: 'warning',
              title: name + '를 입력해주세요',
              onClose() {
                document.getElementById(key)?.focus();
              },
            })
          } else {
            showAlert({
              type: 'warning',
              title: name + '을 입력해주세요',
              onClose() {
                document.getElementById(key)?.focus();
              },
            })
          }
          validateResult = false
        }
      }
    })

    return validateResult
  }

  const insertCode = async (data: CodeDetailItem) => {
    if (!validate(data)) {
      return;
    }
    try {
      const response: any = await axios.post('/comCode/insert', data,)
      if (response.data.common.resultCode == 'CODE0200') {
        setRequestData({ codeType: commonData.codeType });
        getCodeDetail(commonData.codeType);
        setAddMode(false);
      } else {
        showAlert({
          title: '공통코드 저장을 실패했어요.',
        })
      }
    } catch (error) {
      showAlert({
        title: '공통코드 저장을 실패했어요.',
      })
      console.error('Error fetching terms reg:', error)
    }
  }

  // 코드 다건(공통부) 변경
  const updateCode = async () => {
    try {
      if (!validate(commonData)) {
        return;
      }
      const response: any = await axios.post('/comCode/updateList', commonData,)
      if (response.data.common.resultCode == 'CODE0200') {
        navigate('/code', {state : {search : location.state?.search || '', page : location.state?.page || 1, perPage: location.state?.perPage || 10}});
      } else {
        showAlert({
          title: '공통코드 저장을 실패했어요.',
        })
      }
    } catch (error) {
      showAlert({
        title: '공통코드 저장을 실패했어요.',
      })
      console.error('Error fetching terms reg:', error)
    }
  }

  // 코드 단건변경
  const handleButtonUpdateClick = (row: CodeDetailItem) => {
    showConfirm({
      title: '공통코드를 수정할까요?',
      async onConfirm() {
        try {
          if (!validate(row)) {
            return;
          }
          const response: any = await axios.post('/comCode/update', row,)
          if (response.data.common.resultCode == 'CODE0200') {
            showAlert({
              type: 'success',
              title: '공통코드를 정상적으로 수정했어요.',
              onClose() {
                getCodeDetail();
              },
            })
          } else {
            showAlert({
              title: '공통코드 수정을 실패했어요.',
            })
          }
        } catch (error) {
          showAlert({
            title: '공통코드 수정을 실패했어요.',
          })
          console.error('Error fetching terms reg:', error)
        }
      },
    })
  }

  useEffect(() => {
    if (!!requestData.codeType) {
      // 파라미터로 전달된 약관ID값이 있으면 수정, 없으면 등록
      getCodeDetail();
    }
  }, []);

  useEffect(() => {
    if (addMode) {
      changePage(1, true);
    } else {
      changePage(1);
    }

  }, [addMode])

  return (
    <>
      <div style={{ width: '100%', height: '100%', marginTop: '50px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ marginLeft: '20px', fontSize: '20px' }}>공통코드 등록/수정</p>
          <FlexWrapper gap="20px">
            <Button
              onClick={() =>
                showConfirm({
                  title: '공통코드목록 화면으로 이동할까요?',
                  onConfirm() {
                    navigate('/code', {state : {search : location.state?.search || '', page : location.state?.page || 1, perPage: location.state?.perPage || 10}})
                  },
                })
              }
              color="neverbe"
              variant="outlined"
            >
              목록
            </Button>
            <Button
              onClick={() =>
                showConfirm({
                  title: '공통코드를 저장할까요?',
                  onConfirm() {
                    if (!!requestData.codeType) {
                      updateCode();
                    } else {
                      insertCode(commonData);
                    }
                  },
                })
              }
              color="neverbe"
            >
              저장
            </Button>
          </FlexWrapper>
        </div>
      </div>
      <Container>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="termsId">
                코드유형
              </Label>
              <Input
                id="codeType"
                name="codeType"
                type="text"
                placeholder="코드유형을 입력해주세요."
                maxLength={30}
                value={commonData?.codeType || ''}
                disabled={!!codes[0]}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="termsVer">
                코드유형명
              </Label>
              <Input
                id="codeTypeNm"
                name="codeTypeNm"
                type="text"
                placeholder="코드유형명을 입력해주세요."
                maxLength={100}
                value={commonData?.codeTypeNm || ''}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        {
          !codes || codes.length == 0 ?
            <>
              <Row>
                <Col col>
                  <FormGroup mb="20px">
                    <Label display="block" mb="5px" htmlFor="termsTit">
                      코드
                    </Label>
                    <Input
                      id="code"
                      name="code"
                      type="text"
                      placeholder="코드를 입력해주세요."
                      maxLength={15}
                      value={commonData?.code || ''}
                      disabled={false}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col col>
                  <FormGroup mb="20px">
                    <Label display="block" mb="5px" htmlFor="termsCont">
                      코드명
                    </Label>
                    <Input
                      id="codeNm"
                      name="codeNm"
                      type="text"
                      placeholder="코드명을 입력해주세요."
                      maxLength={100}
                      value={commonData?.codeNm}
                      disabled={false}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col col>
                  <FormGroup mb="20px">
                    <Label display="block" mb="5px" htmlFor="termsTit">
                      코드영문명
                    </Label>
                    <Input
                      id="engCodeNm"
                      name="engCodeNm"
                      type="text"
                      placeholder="코드영문명을 입력해주세요."
                      maxLength={100}
                      value={commonData?.engCodeNm || ''}
                      disabled={false}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col col>
                  <FormGroup mb="20px">
                    <Label display="block" mb="5px" htmlFor="termsCont">
                      화면표시순서
                    </Label>
                    <Input
                      id="displayOrder"
                      name="displayOrder"
                      type="number"
                      placeholder="화면표시순서를 입력해주세요."
                      value={commonData?.displayOrder || 1}
                      disabled={false}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col col>
                  <FormGroup mb="20px">
                    <Label display="block" mb="5px" htmlFor="termsGubn">
                      사용여부
                    </Label>
                    <Select
                      id="useYn"
                      name="useYn"
                      value={commonData?.useYn}
                      onChange={handleInputChange}
                    >
                    {getComCodes('USE_YN').map((item) => (
                      <option key={'useYn'+item.code} value={item.code}>
                          {item.codeNm}
                      </option>
                    ))}
                    </Select>
                  </FormGroup>
                </Col>
                <Col col>
                  <FormGroup mb="20px">
                    <Label display="block" mb="5px" htmlFor="termsTit">
                      비고
                    </Label>
                    <Input
                      id="remark"
                      name="remark"
                      type="text"
                      placeholder="비고를 입력해주세요."
                      maxLength={4000}
                      value={commonData?.remark || ''}
                      disabled={false}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>

            </> : <></>
        }
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="termsTit">
                메인테이블
              </Label>
              <Input
                id="mainTable"
                name="mainTable"
                type="text"
                placeholder="메인테이블을 입력해주세요."
                maxLength={250}
                value={commonData?.mainTable || ''}
                disabled={false}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="termsCont">
                메인컬럼
              </Label>
              <Input
                id="mainColumn"
                name="mainColumn"
                placeholder="메인컬럼을 입력해주세요."
                value={commonData?.mainColumn || ''}
                disabled={false}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        {/* TODO 사용할까?
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="termsTit">
                상위코드유형
              </Label>
              <Input
                id="mainTable"
                name="mainTable"
                type="text"
                placeholder="상위코드유형을 입력해주세요."
                maxLength={250}
                value={code[0]?.upCodeType}
                disabled={false}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="termsCont">
                상위코드
              </Label>
              <Input
                id="termsCont"
                name="termsCont"
                placeholder="상위코드를 입력해주세요."
                value={code[0]?.upCode}
                disabled={false}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row> */}
        {
          codes && codes.length > 0 ?
            <Row>
              <Button
                onClick={() => {
                  setAddMode(!addMode);
                }}

                color="neverbe"
              >{addMode ? '취소' : '추가'}
              </Button>
            </Row> : <></>
        }
      </Container>
      {
        codes && codes.length > 0 ?
          <>
            <DataTable
              columns={[
                {
                  name: '코드',
                  selector: (row) => row.code,
                  // sortable: true,
                  cell: (row, index) => {
                    const idx = (currentPage - 1) * perPage + index;
                    return <>
                      {
                        addMode && idx == 0 ?
                          <Input
                            id={'code' + idx}
                            name="code"
                            value={row.code || ''}
                            onChange={(e) => {
                              handleRowChange(e, idx);
                            }}
                          /> : <>{row.code}</>
                      }
                    </>
                  },
                  center: true,
                  wrap: true,
                  width: '10%',
                },
                {
                  name: '코드명',
                  selector: (row) => row.codeNm,
                  // sortable: true,
                  cell: (row, index) => {
                    const idx = (currentPage - 1) * perPage + index;
                    return <>
                      <Input
                        id={'codeNm' + idx}
                        name="codeNm"
                        value={row.codeNm || ''}
                        onChange={(e) => {
                          handleRowChange(e, idx);
                        }}
                      />
                    </>
                  },
                  center: true,
                  wrap: true,
                  width: '15%',
                },
                {
                  name: '코드영문명',
                  selector: (row) => row.engCodeNm,
                  // sortable: true,
                  cell: (row, index) => {
                    const idx = (currentPage - 1) * perPage + index;
                    return <>
                      <Input
                        id={'engCodeNm' + idx}
                        name="engCodeNm"
                        value={row.engCodeNm || ''}
                        onChange={(e) => {
                          handleRowChange(e, idx);
                        }}
                      />
                    </>
                  },
                  center: true,
                  wrap: true,
                  width: '15%',
                },
                {
                  name: '화면표시순서',
                  selector: (row) => row.displayOrder,
                  // sortable: true,
                  cell: (row, index) => {
                    const idx = (currentPage - 1) * perPage + index;
                    return <>
                      <Input
                        id={'displayOrder' + idx}
                        name="displayOrder"
                        type="number"
                        value={row.displayOrder}
                        onChange={(e) => {
                          handleRowChange(e, idx);
                        }}
                      />
                    </>
                  },
                  center: true,
                  wrap: true,
                  width: '10%',
                },
                {
                  name: '비고',
                  selector: (row) => row.remark,
                  // sortable: true,
                  cell: (row, index) => {
                    const idx = (currentPage - 1) * perPage + index;
                    return <>
                      <Input
                        id={'remark' + idx}
                        name="remark"
                        value={row.remark || ''}
                        onChange={(e) => {
                          handleRowChange(e, idx);
                        }}
                      />
                    </>
                  },
                  center: true,
                  wrap: true,
                  width: '20%',
                },
                {
                  name: '사용여부',
                  selector: (row) => row.useYn,
                  // sortable: true,
                  cell: (row, index) => {
                    const idx = (currentPage - 1) * perPage + index;
                    return <>
                      <Select
                        id={'useYn' + idx}
                        name="useYn"
                        value={row.useYn}
                        onChange={(e) => {
                          handleRowChange(e, idx);
                        }}
                      >
                      {getComCodes('USE_YN').map((item) => (
                        <option key={'useYn'+idx+item.code} value={item.code}>
                            {item.codeNm}
                        </option>
                      ))}
                      </Select>
                    </>
                  },
                  center: true,
                  wrap: true,
                  width: '10%',
                },
                {
                  name: '',
                  cell: (row, index) => {
                    const idx = (currentPage - 1) * perPage + index;
                    return <>
                      <Button
                        variant="outlined"
                        color="neverbe"
                        size="xs"
                        onClick={() => {
                          if (addMode && idx == 0) {
                            showConfirm({
                              title: '공통코드를 추가할까요?',
                              onConfirm() {
                                insertCode(row);
                              },
                            })
                          } else {
                            handleButtonUpdateClick(row)
                          }
                        }}
                      >
                        {addMode && idx == 0 ? '저장' : '수정'}
                      </Button>
                    </>
                  },
                  sortable: false,
                  width: '10%',
                },
                {
                  name: '등록자',
                  selector: (row) => row.regId,
                  // sortable: true,
                  center: true,
                  wrap: true,
                  width: '10%',
                },
                {
                  name: '등록일시',
                  selector: (row) => commonUtils.formatDate(row.regDate, true),
                  // sortable: true,
                  center: true,
                  wrap: true,
                  width: '15%',
                },
                {
                  name: '수정자',
                  selector: (row) => row.updId,
                  // sortable: true,
                  center: true,
                  wrap: true,
                  width: '10%',
                },
                {
                  name: '수정일시',
                  selector: (row) => commonUtils.formatDate(row.updDate, true),
                  // sortable: true,
                  center: true,
                  wrap: true,
                  width: '15%',
                },
              ]}
              data={codes}
              // customStyles={tableStyles}
              onChangePage={page => changePage(page)}
              pagination={true}
              paginationPerPage={perPage}
              onChangeRowsPerPage={page => setPerPage(page)}
              paginationRowsPerPageOptions={[10, 50, 100, 500]}
              paginationDefaultPage={currentPage}
              noDataComponent={<>조회결과가 없습니다.</>}
            />
          </>
          : <></>
      }
    </>
  )
}
export default DetailComCode;
